import React, { useEffect, useState, useRef } from 'react'
import { Box, Flex, useDisclosure, Text, Button } from '../../vanilla'
import { Hit, ProductModel } from '../../store/ProductStore'
import {observer} from 'mobx-react-lite'
import { Link } from '../link'
import { HeartFilledIcon, HeartIcon } from '../icons'
import { useItemPrice } from '../algolia/util/useItemPrice'
import { ReviewStars } from '../review-stars'
import { PDPBadgeAsset } from '../cms/product-badges'
import { useAlcoholRestrictions } from '../../hooks/use-alcohol-restrictions'
import { useLocation } from 'react-router-dom'
import { useAlgoliaAnalytics, useBookDeliveryPopover } from '../../contexts'
import { useSelectItemTracking } from '../../analytics/select-item-tracking'
import { Price } from '../price'
import { mobilePLPBadges } from '../algolia/instantsearch/styles.css'
import { ATBButtonPopover } from '../add-to-basket-button-popover'
import { useBasketItem } from '../../store/hooks/useBasketItem'
import { LearnMoreAlcoholRestrictionModal } from '../alcohol-restriction-modal'
import { EVENT_NAMES_MAP } from '../../analytics/algolia'
import { CART_LOCATION } from '../../analytics/utils'
import { useAddToCartTracking } from '../../analytics/click-event-tracking'
import { useSlotExpiration } from '../../store/hooks/useSlotExpiration'
import { recommenderHit } from '../recommendations/styles.css'
import { formatProductName, isMobile, getDisabledButtonText, retainBrowsingPosition, isProductPurchaseDisabled } from '../../utils/utils'
import { promotionModalContent } from '../product-detail/product-promo/styles.css'
import { ProductImage } from '../product-detail/product-image'
import { TopRightProductBadgeGroup } from '../top-right-product-badges/top-right-product-badge-group'
import { ProductItem } from '../../store/BasketStore'
import { transformHit } from '../algolia/util/transformHit'
import { HitItemProps } from '../../types/algolia'
import { SponsoredProductPopover } from '../sponsored-product-popover'
import { sendRMImpressions, sendRMClicks } from '../../analytics/retail-media-tracking'
import { useIntersectionObserver } from '../../hooks/use-intersection-observer'
import { MultibuyPromoBadge } from '../product-detail/product-promo/multibuy-badge'
import { flowResult } from 'mobx'
import {
  MultiBuyPromoOfferMessage,
  NearPromo,
} from '../product-detail/product-promo'
import {
  useBasketStore,
  useOrderStore,
  useCustomerStore,
  useGlobalStore,
  useRetailMediaStore,
  useContentStore
} from '../../store/hooks/useStore'
import LazyLoad from 'react-lazyload'
import useEinstein from '../../store/hooks/useEinstein'
import useWishlistItem from '../../store/hooks/useWishlistItem'
import UpdateCartItemQuantity from '../update-cart-quantity'
import useCustomSitePreference from '../../hooks/forms/useCustomSitePreference'
import { ViewAlternativesButton } from '../view-alternatives-button'
import { ProductOverlay } from '../product-overlay'

const ProductTileContainer = ({
  hit,
  className,
  isPLPTile,
  idx,
  hitItemIndex,
  itemListName,
  itemListId,
  einsteinRecoParams,
  viewingCategoryId,
  showBadges = true,
  showPromotions = true,
  showRating = true,
  searchQuery,
  showNearPromo,
  isSponsored,
  sponsoredAdId
}: HitItemProps) => {
  const learnMoreAlcRestrictionModal = useDisclosure()
  const {events} = useAlgoliaAnalytics()
  const {sendViewProduct, sendClickRecommendations, sendClickCategoryProduct} = useEinstein()
  const basketStore = useBasketStore()
  const {pathname, search} = useLocation()
  const {
    setShowCheckoutSummary,
    customSitePreferences: {showMultibuyPriceBadge},
    selectedPriceDayOffset,
    selectedStoreId,
    selectedRegion
  } = useGlobalStore()

  // Restricted product classes
  const restrictedProductInfo = basketStore.getRestrictedProductInfo(hit.productClasses, hit.id)

  const {displayPrice, promotionPrice} = useItemPrice(hit as unknown as ProductModel)
  const {handleRemoveWishlistItem, isInWishlist, handleAddWishlistItem} = useWishlistItem(hit.id)
  const {checkIfProductAlcohol, isCurrentSlotAlcoholRestricted} = useAlcoholRestrictions()
  const isAlcohol = checkIfProductAlcohol(hit)
  const ref = useRef<HTMLDivElement | null>(null)
  // @todo format the algolia data to be consistent across PLP and PDP - happens in price component too
  // @ts-ignore
  const img = hit.image_groups?.[0]?.images?.[0] || hit.imageGroups?.[0]?.images?.[0]
  const {quantity} = useBasketItem(hit.id)
  const disabledProductBecauseOfRestriction = (isAlcohol && isCurrentSlotAlcoholRestricted) || (restrictedProductInfo.cannotAddToBasket && quantity === 0)
  const [clickedQuantity, setClickedQuantity] = useState(0)
  const {showBookDeliveryPopover} = useBookDeliveryPopover()
  const isBeforeYouGoPage = pathname === '/Iceland-BeforeYouGoPages'
  const {isReservationExpired, handleEditReservationExpired} = useSlotExpiration()
  const {editMode} = useOrderStore()
  const hasBadgeAsset = !!hit.pdpBadgeAsset
  const {getApplicablePromotions} = useCustomerStore()
  const {multiBuyPromo, nearPromo, bundle} = getApplicablePromotions(hit.productPromotions)
  const {retailMediaUrl} = useRetailMediaStore()
  const [isRMImpressionsSent, setIsRMImpressionsSent] = useState(false)

  const {isOnScreen: isTrueView, observerKey} = useIntersectionObserver(ref, {
    useOnce: true,
    /* Offset for intersection track. Sometimes a user can view the top of the product list section on a PDP,
    but the actual banners are still not visible, so adding an offset to ensure that the event only fires when the user has scrolled to and viewed the product items,
    only then change observer state. */
    rootMargin: '-150px 0px -150px 0px',
  })

  useEffect(() => {
    setClickedQuantity(quantity)
  }, [basketStore.count])

  useEffect(() => {
    if (isTrueView && sponsoredAdId && !isRMImpressionsSent) {
      setIsRMImpressionsSent(true)
      sendRMImpressions(sponsoredAdId, retailMediaUrl)
    }
  }, [hit, isTrueView])

  const isPurchaseDisabled = isProductPurchaseDisabled(!!displayPrice, hit.availableForDayAtStore, hit.isAddToCartDisabled)

  const addToBasket = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (disabledProductBecauseOfRestriction) {
      return
    }

    if (editMode && isReservationExpired()) {
      // If in edit mode and slot has expired send to order history and don't add to basket
      handleEditReservationExpired()
    } else if (!basketStore.canAddItems) {
      showBookDeliveryPopover({
        clickedHitId: hit.id,
        bookDeliveryProps: {
          product: hit as ProductItem,
        },
      })
    } else {
      if (isBeforeYouGoPage) {
        setShowCheckoutSummary(true)
      }
      basketStore.addProductToUpdate({
        product: hit as ProductItem,
        quantity: 1,
        event: EVENT_NAMES_MAP.PLP_PRODUCT_ADD_TO_CART_AFTER_SEARCH,
        queryID: hit.__queryID,
      })
      setClickedQuantity(1)
    }
  }

  const productLink = `/p/${formatProductName(hit?.name)}/${hit.id}.html`

  const sendProductClickEvents = () => {
    /** Einstein Analytics events **/
    if (isPLPTile && viewingCategoryId)
      sendClickCategoryProduct({
        product: {id: hit.id},
        category: {
          id: viewingCategoryId,
        },
      })
    else if (einsteinRecoParams)
      sendClickRecommendations({
        product: {id: hit.id},
        __recoUUID: einsteinRecoParams.recoUUID,
        recommenderName: einsteinRecoParams.recommender,
      })
    else sendViewProduct({product: {id: hit.id}})

    /** Algolia Analytics events  **/
    events?.productListPage?.sendClickEvent({
      queryID: hit.__queryID,
      positions: [hit.__position],
      objectIDs: [hit.objectID],
    })
  }

  const toggleWishlist = async () => {
    if (isInWishlist) {
      handleRemoveWishlistItem()
      return
    }

    await handleAddWishlistItem(locationOfCart, viewingCategoryId, location.search, hitItemIndex)

    /** Algolia Analytics events  **/
    if (hit.__queryID) {
      events?.productListPage?.sendAddToFavouriteAfterSearchEvent({
        queryID: hit.__queryID,
        objectIDs: [hit.objectID],
      })
    } else {
      events?.productListPage?.sendAddToFavouriteEvent({
        objectIDs: [hit.objectID],
      })
    }
  }

  const showReviews = useCustomSitePreference('ShowReviews')
  const shouldShowRating = showRating && showReviews
  const showMultiBuyPromoBadge = Boolean(showMultibuyPriceBadge) && !!multiBuyPromo && multiBuyPromo?.multibuyUnitPrice
  const sendSelectItemData = useSelectItemTracking()
  const sendAddToCartData = useAddToCartTracking(basketStore.basket)
  const isMobileFlag = isMobile()
  const cms = useContentStore()
  const getCartLocation = () => {
    if (typeof location != "undefined") {
      if (search) {
        return CART_LOCATION.SEARCH
      }
      if (location.pathname === '/Iceland-BeforeYouGoPages') {
        return CART_LOCATION.CHECKOUT
      }
      if (className === recommenderHit) {
        return CART_LOCATION.CAROUSEL
      }
      if (className === promotionModalContent) {
        return CART_LOCATION.CAROUSEL
      }
      return CART_LOCATION.PLP
    }
  }

  const locationOfCart = getCartLocation()
  let badges: object[] = []
  const taggingInfo = { viewingCategoryId, itemListName, itemListId}
  ;(async function getBadgesData() {
    badges =  await flowResult(cms.fetchBadgesForHits([hit]))
  })()

  return (
    <Box position="relative" className={className} ref={ref} key={observerKey} data-tile-index={hitItemIndex}>
      <Button
        aria-label="Add to favourites"
        variant="unstyled"
        position="absolute"
        right="0px"
        top="0px"
        data-test-selector="favourite-icon"
        onClick={toggleWishlist}
      >
        {isInWishlist ? (
          <HeartFilledIcon color="accent0" boxSize="20px" />
        ) : (
          <HeartIcon color={{default: 'black', hover: 'accent0'}} boxSize="20px" />
        )}
      </Button>
      {/* Top Right Badges on mobile */}
        {isMobileFlag && (
          <TopRightProductBadgeGroup
            hit={hit}
            className={mobilePLPBadges}
            top={[15, 55, 95]}
            right={-5}
          />
        )}
      <Flex
        flexDirection={[isPLPTile ? 'row' : 'column', 'column']}
        flexWrap="wrap"
        align={['flex-start', 'center']}
        paddingX="8px"
        paddingY="12px"
        data-test-selector="product-list-item"
        bg="white"
        height="full"
      >
        <Flex flexDirection='column' alignItems='center' gap={['14px', '4px']}>
          <Box
            aria-label={hit.name}
            as={Link}
            href={productLink}
            ratio="1"
            width="full"
            style={{
              maxWidth: 160,
              flexBasis: 148,
              ...(disabledProductBecauseOfRestriction && {
                pointerEvents: 'none',
              }),
            }}
            marginTop={['0px', '32px']}
            flexShrink="0"
            position="relative"
            onClickCapture={() => {
              sendProductClickEvents(), sendSelectItemData(hit, hitItemIndex, {...taggingInfo, badges}), retainBrowsingPosition()
              if (sponsoredAdId) sendRMClicks(sponsoredAdId, retailMediaUrl);
            }}
          >
            <ProductOverlay 
              isAlcoholRestriction={isAlcohol && disabledProductBecauseOfRestriction}
              isUnavailable={isPurchaseDisabled}
              isRestricted={restrictedProductInfo?.cannotAddToBasket && hit.productClasses?.length > 0}
              onAlcoholLearnMoreClick={learnMoreAlcRestrictionModal.onToggle}
              hit={hit}
            />
            {/* Top Right Badges on desktop */}
            {!isMobileFlag && (
              <TopRightProductBadgeGroup
                hit={hit}
                top={[15, 55, 95]}
                right={-5}
              />
            )}

            {typeof idx === 'number' && idx <= 5 ? (
              <ProductImage
                image={img}
                imageAlt={img?.alt}
                isThumbnail={false}
                inDrawer={false}
                display="block"
                width="full"
                height="auto"
                marginTop={['12px', '0px']}
                paddingRight="8px"
                style={{maxHeight: 160, objectFit: 'contain'}}
                data-test-selector="product-list-item-image"
              />
            ) : (
              <LazyLoad>
                <ProductImage
                  image={img}
                  imageAlt={img?.alt}
                  isThumbnail={false}
                  inDrawer={false}
                  display="block"
                  width="full"
                  height="auto"
                  marginTop={['12px', '0px']}
                  paddingRight="8px"
                  style={{maxHeight: 160, objectFit: 'contain'}}
                  data-test-selector="product-list-item-image"
                />
              </LazyLoad>
            )}
          </Box>
          {/* Sponsored Product Message */}
            {isSponsored ?
              <Flex alignItems="center" gap="4px" paddingX='8px' paddingY='2px'>
                <Text variant='text2' color='gray800'>Sponsored</Text>
                <SponsoredProductPopover />
              </Flex>
            : null}
          </Flex>
        <Flex
          align={['flex-start', 'center']}
          flexBasis={['100px']}
          flexGrow="1"
          flexShrink={['0', '1', '1']}
          marginTop={['0px', '20px']}
          marginLeft={['12px', '0px']}
          flexDirection={'column'}
          height={['auto', 'full']}
          gap="12px"
          justifyContent="space-between"
        >
          <Text
            as={Link}
            aria-label={hit.name}
            href={productLink}
            variant="text4"
            textAlign={['left', 'center']}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
            overflow="hidden"
            color={{default: 'gray800', hover: 'gray700'}}
            marginTop={['32px', '0px']}
            marginBottom={['12px', '0px']}
            marginRight={['36px', '0px']}
            lineHeight="short"
            data-test-selector="product-list-item-name"
            onClickCapture={() => {
              sendProductClickEvents(), sendSelectItemData(hit, hitItemIndex, {...taggingInfo, badges}), retainBrowsingPosition()
              if (sponsoredAdId) sendRMClicks(sponsoredAdId, retailMediaUrl);
            }}
          >
            {hit.name}
          </Text>

          <Flex
            flexDirection="column"
            gap="12px"
            alignItems={['flex-start', 'center']}
            justifyContent="space-between"
          >
            {shouldShowRating && hit.productRating ? (
              <ReviewStars rating={hit.productRating} />
            ) : (
              <Box height="20px" display={['none', 'block']} />
            )}
            {/* Near promo */}
            {showPromotions
              && (nearPromo || bundle)
              && showNearPromo !== false
                ? <NearPromo
                    data-test-selector="product-list-item-promo-badge"
                    promotion={nearPromo}
                    bundle={bundle}
                    hitId={hit.id}
                  />
                : null
            }
            <Price
              price={displayPrice}
              promotionPrice={promotionPrice}
              priceInfo={hit.priceInfo}
              isPLPTile={isPLPTile}
            />
            {showPromotions && showMultiBuyPromoBadge &&
              <MultibuyPromoBadge promotion={multiBuyPromo} />
            }
          </Flex>
          {/* Badges: text or image */}
          {showBadges && hit.pdpBadgeAsset ? <PDPBadgeAsset badgeKey={hit.pdpBadgeAsset} /> : null}
        </Flex>
          {/* Nearness +x message */}
          {showPromotions && nearPromo ? (
            <Box paddingTop="12px" marginLeft={["auto", "0px"]} marginTop={['auto', '0px']}>
              <MultiBuyPromoOfferMessage promotion={nearPromo} productId={hit.id} />
            </Box>
          ) : null}
        <Box
          width={[isPLPTile ? 'full' : 'auto', 'auto']}
          display={[isPLPTile ? 'flex' : 'block', 'block']}
          justifyContent="flex-end"
          marginTop={hasBadgeAsset ? ['8px', '16px'] : ['8px', '24px']}
          height='auto'
          minHeight={['auto', '56px']}
          paddingBottom={['8px', '4px']}
        >
          {isPurchaseDisabled ? (
            <>
             {!disabledProductBecauseOfRestriction ? (
                <ViewAlternativesButton 
                  product={hit} 
                  displayPrice={displayPrice}
                  isPLPTile={isPLPTile}
                  disabledButtonOverride={
                    <Box>
                      <Button disabled variant="secondary" size='smWide' width={['auto', '150px']}>
                        {getDisabledButtonText(hit.disabledStatus)}
                      </Button>
                    </Box>
                  } 
                />
              ) : null}
            </>
          ) : (
            <UpdateCartItemQuantity
              product={hit as ProductItem}
              quantity={clickedQuantity || 0}
              setQuantity={setClickedQuantity}
              maxQuantity={restrictedProductInfo?.maxQuantityAllowed}
              cartLocation={locationOfCart}
              viewingCategoryId={viewingCategoryId}
              searchQuery={searchQuery}
              hitItemIndex={hitItemIndex}
              restrictionModal={learnMoreAlcRestrictionModal}
              isSponsored={isSponsored}
              renderButton={
                !disabledProductBecauseOfRestriction ? (
                  <ATBButtonPopover
                    hit={transformHit(hit, selectedPriceDayOffset, selectedStoreId, selectedRegion) as Hit}
                    displayPrice={displayPrice}
                    isPLPTile={isPLPTile}
                    ATBEventDetails={{
                      locationOfCart: locationOfCart || '',
                      viewingCategoryId: viewingCategoryId || '',
                      searchQuery: searchQuery || '',
                    }}
                    onButtonClick={(e) => {
                      addToBasket(e)
                      sendAddToCartData(
                        hit,
                        locationOfCart,
                        viewingCategoryId,
                        searchQuery,
                        hitItemIndex
                      )
                    }}
                    hitItemIndex={hitItemIndex}
                    width={['auto', '150px']}
                    alignSelf="flex-end"
                  />
                ) : undefined
              }
            />
          )}
        </Box>
      </Flex>
      {isAlcohol && disabledProductBecauseOfRestriction ? (
        <LearnMoreAlcoholRestrictionModal {...learnMoreAlcRestrictionModal} />
      ) : null}
    </Box>
  )
}

export const ProductTile = observer(ProductTileContainer)